import { useEffect, useState } from "react";
import {
  Divider,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Typography,
} from "../../../../components";
import { OrdemServicoService, PessoaService } from "../../../../services";
import { formatPrice, responseErros } from "../../../../utils";
const ordemServicoService = new OrdemServicoService();
const pessoaService = new PessoaService();

const CORES_RANK = ["#f59e0b", "#94a3b8", "#b45309"];

function BarraProgresso({ valor, max, cor, label }) {
  const pct = max > 0 ? (valor / max) * 100 : 0;
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        sx={{
          flex: 1,
          height: 8,
          borderRadius: 4,
          backgroundColor: "#f1f5f9",
          overflow: "hidden",
          minWidth: 48,
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: 4,
            backgroundColor: cor,
            transition: "width 0.3s",
          }}
        />
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: "#1e293b",
          minWidth: 40,
          textAlign: "right",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function MonitorOsProdutividade({ filtrosBody }) {
  const [loading, setLoading] = useState(false);
  const [tabela, setTabela] = useState([]);
  const [prestadores, setPrestadores] = useState([]);

  useEffect(() => {
    buscarPrestadores();
  }, []);

  useEffect(() => {
    buscarProdutividade();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosBody]);

  async function buscarPrestadores() {
    const result = await pessoaService.getAll({
      prestadorServico: true,
      nonPaginated: true,
    });
    if (!result.isAxiosError) {
      setPrestadores(result.data?.rows || result.data || []);
    }
  }

  async function buscarProdutividade() {
    setLoading(true);
    const result = await ordemServicoService.getRelatorioDashboardProdutividade(
      filtrosBody,
    );
    if (!result.isAxiosError) {
      setTabela(result.data?.tabela || []);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const maxOS = Math.max(...tabela.map((r) => r.qtdServicos || 0), 0);
  const maxHoras = Math.max(...tabela.map((r) => r.horasApontadas || 0), 0);

  return (
    <Card sx={{ borderRadius: 3, border: "1px solid #f0f0f0", height: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Produtividade da Equipe
          </Typography>
          <i
            className="ph-bold ph-users-three"
            style={{ color: "#64748b", fontSize: 20 }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <Box>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={40}
                sx={{ mb: 1, borderRadius: 1 }}
              />
            ))}
          </Box>
        ) : tabela.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
            gap={1}
          >
            <i
              className="ph-bold ph-users-three"
              style={{ fontSize: 32, color: "#94a3b8" }}
            />
            <Typography variant="body2" color="text.secondary">
              Nenhuma O.S concluída no período selecionado.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#f8fafc" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 44 }} align="center">
                  #
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>
                  Prestador de Serviço
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>
                  OS Concluídas
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>
                  Horas Apontadas
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Valor Gerado
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tabela.map((row, i) => (
                <TableRow
                  key={row.prestadorId || i}
                  sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                >
                  <TableCell align="center">
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        mx: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                        color: i < 3 ? "#fff" : "#475569",
                        backgroundColor: i < 3 ? CORES_RANK[i] : "#e2e8f0",
                      }}
                    >
                      {i + 1}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar id={row.prestadorId} userList={prestadores} />
                      <Typography
                        variant="body2"
                        sx={{ color: "#1e293b" }}
                        title={row.nome}
                      >
                        {row.nome}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <BarraProgresso
                      valor={row.qtdServicos}
                      max={maxOS}
                      cor="#1976d2"
                      label={row.qtdServicos}
                    />
                  </TableCell>
                  <TableCell>
                    <BarraProgresso
                      valor={row.horasApontadas}
                      max={maxHoras}
                      cor="#388e3c"
                      label={`${(row.horasApontadas || 0).toLocaleString(
                        "pt-BR",
                        { maximumFractionDigits: 1 },
                      )} h`}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, color: "#1e293b" }}
                  >
                    {formatPrice(row.totalValorServico)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default MonitorOsProdutividade;
